import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { parseMediaPlayerConfigFromAsset } from '~/shared/utils/assetHelper';
import { ImageAsset } from '~/techstyle-shared/react-assets';
import {
  desktop,
  mobile,
  useBreakpoint,
  Video,
} from '~/techstyle-shared/react-components';

const SplitScreenHeroBannerContainer = styled.div`
  ${desktop`
    display: flex;
    flex-direction: row;
    align-items: center;
  `}
`;

// the css hack to overcome a nasty white 1-pixel defect of a Vimeo player
const onePixelSideCroppedViewport = css`
  overflow: hidden;
  > div {
    width: calc(100% + 2px);
    height: calc(100% + 1px);
    margin: 0 -1px -2px;
  }
`;

const LeftSideContainer = styled.div`
  flex-shrink: 0;
  flex-basis: 54.6%;

  ${mobile`${onePixelSideCroppedViewport}`}
`;

const RightSideContainer = styled.div`
  flex-grow: 1;
`;

const StyledImageAsset = styled(ImageAsset)`
  width: 100%;
  img {
    ${desktop`
      width: 100%;
  `}
  }
`;

export const SplitScreenHeroBanner = ({
  videoAspectRatio,
  videoId,
  asset,
  parentRefs: { wrapperRef: carouselWrapperRef, debouncedFlickityReset },
}) => {
  const { isDesktop } = useBreakpoint();
  const leftSideContainerRef = useRef();
  const rightSideContainerRef = useRef();

  const adjustHeroBannerSize = useCallback(
    (height) => {
      if (!height) {
        return;
      }

      const leftSideContainer = leftSideContainerRef.current;
      const rightSideContainer = rightSideContainerRef.current;

      if (leftSideContainer) {
        // This is the calculation of width of a video according to aspect ratio of the video.
        leftSideContainer.style.flexBasis = `${height * videoAspectRatio}px`;

        if (rightSideContainer) {
          // This is a workaround for not being able to get a reference to an ImageAsset component
          // and that static height style of 100% doesn't work for this layout.
          rightSideContainer.children[0].style.height = `${height}px`;
        }
      }
    },
    [videoAspectRatio]
  );

  const resetHeroBannerSize = useCallback(() => {
    const leftSideContainer = leftSideContainerRef.current;
    const rightSideContainer = rightSideContainerRef.current;

    if (leftSideContainer) {
      leftSideContainer.style.flexBasis = '';

      if (rightSideContainer) {
        rightSideContainer.children[0].style.height = '';
      }
    }
  }, []);

  // adjust HeroBanner on load
  const [windowWidth, setWindowWidth] = useState();
  useLayoutEffect(() => {
    if (isDesktop && windowWidth) {
      const carouselHeight = carouselWrapperRef.current?.offsetHeight;

      adjustHeroBannerSize(carouselHeight);
    }
  }, [carouselWrapperRef, adjustHeroBannerSize, isDesktop, windowWidth]);

  // adjust HeroBanner on resize
  useEffect(() => {
    const handleResize = () => {
      if (windowWidth > window.innerWidth) {
        resetHeroBannerSize();
        debouncedFlickityReset();
      }
      setWindowWidth(window.innerWidth);
    };

    if (isDesktop && typeof window !== 'undefined') {
      setWindowWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);

      return () => window.removeEventListener('resize', handleResize);
    }
  }, [
    windowWidth,
    setWindowWidth,
    isDesktop,
    resetHeroBannerSize,
    debouncedFlickityReset,
  ]);

  return (
    <SplitScreenHeroBannerContainer>
      <LeftSideContainer ref={leftSideContainerRef}>
        <Video
          aspectRatio={videoAspectRatio}
          url={Video.getUrlFromVideoId(videoId)}
          config={parseMediaPlayerConfigFromAsset({ asset })}
        />
      </LeftSideContainer>
      <RightSideContainer ref={rightSideContainerRef}>
        <StyledImageAsset asset={asset} background={isDesktop} />
      </RightSideContainer>
    </SplitScreenHeroBannerContainer>
  );
};

SplitScreenHeroBanner.propTypes = {
  asset: PropTypes.object,
  parentRefs: PropTypes.object,
  videoAspectRatio: PropTypes.string,
  videoId: PropTypes.string,
};
